import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../Components/SEOHead';
import { FaHome, FaSearch, FaPhone } from 'react-icons/fa';

const NotFoundPage = () => {
  return (
    <>
      <SEOHead 
        title="Page Not Found | 404 Error - Drosh The Pod Manchester"
        description="The page you're looking for doesn't exist. Return to our homepage or contact us for podiatry services in Manchester."
        canonical="https://www.droshpod.com/404"
      />
      
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto"
          >
            {/* 404 Number */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="mb-8"
            >
              <h1 className="text-8xl md:text-9xl font-bold text-secondary mb-4">
                404
              </h1>
            </motion.div>

            {/* Error Message */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Page Not Found
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Sorry, the page you're looking for doesn't exist or has been moved.
              </p>
              <p className="text-gray-500">
                Don't worry though - you can still find the podiatry services you need!
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            >
              <Link 
                to="/"
                className="bg-secondary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary transition-colors duration-300 flex items-center justify-center"
              >
                <FaHome className="mr-2" />
                Go Home
              </Link>
              
              <Link 
                to="/services"
                className="border-2 border-secondary text-secondary px-8 py-3 rounded-full font-semibold hover:bg-secondary hover:text-white transition-colors duration-300 flex items-center justify-center"
              >
                <FaSearch className="mr-2" />
                View Services
              </Link>
              
              <a 
                href="tel:07809267586"
                className="border-2 border-gray-600 text-gray-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-600 hover:text-white transition-colors duration-300 flex items-center justify-center"
              >
                <FaPhone className="mr-2" />
                Call Us
              </a>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-white p-8 rounded-2xl shadow-lg max-w-4xl mx-auto"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Popular Pages
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link 
                  to="/about"
                  className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-300 text-center"
                >
                  <h4 className="font-semibold text-gray-800 mb-1">About Us</h4>
                  <p className="text-sm text-gray-600">Learn about our clinic</p>
                </Link>
                
                <Link 
                  to="/services"
                  className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-300 text-center"
                >
                  <h4 className="font-semibold text-gray-800 mb-1">Services</h4>
                  <p className="text-sm text-gray-600">View our treatments</p>
                </Link>
                
                <Link 
                  to="/orthotics"
                  className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-300 text-center"
                >
                  <h4 className="font-semibold text-gray-800 mb-1">Orthotics</h4>
                  <p className="text-sm text-gray-600">Custom foot solutions</p>
                </Link>
                
                <Link 
                  to="/consultation-form"
                  className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors duration-300 text-center"
                >
                  <h4 className="font-semibold text-gray-800 mb-1">Book Consultation</h4>
                  <p className="text-sm text-gray-600">Schedule appointment</p>
                </Link>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-8 text-center"
            >
              <p className="text-gray-600 mb-2">
                Need help? Contact us directly:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm text-gray-500">
                <a 
                  href="tel:07809267586"
                  className="hover:text-secondary transition-colors"
                >
                  📞 07809 267586
                </a>
                <a 
                  href="mailto:info@droshpod.com"
                  className="hover:text-secondary transition-colors"
                >
                  ✉️ info@droshpod.com
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default NotFoundPage;
